import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Save, Check, Users, Store, Layers, ShieldCheck as ShieldIcon, Square } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const ROLE_LABELS = { manager: "Manager", field_executive: "Field Executive", staff: "Staff" };

export default function RolePermissions() {
  const [subjectType, setSubjectType] = useState("staff"); // staff | branch | role
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState("");
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [schema, setSchema] = useState({ modules: [], actions: [] });
  const [perms, setPerms] = useState({});
  const [params] = useSearchParams();

  useEffect(() => {
    (async () => {
      const [u, b, s] = await Promise.all([
        api.get("/users"),
        api.get("/branches"),
        api.get("/permissions/schema"),
      ]);
      setUsers(u.data);
      setBranches(b.data);
      setSchema(s.data);
      const urlUser = params.get("user");
      const urlBranch = params.get("branch");
      if (urlBranch) {
        setSubjectType("branch");
        setSelected(urlBranch);
      } else if (urlUser) {
        setSubjectType("staff");
        setSelected(urlUser);
      } else {
        const first = u.data.find((x) => x.role === "staff") || u.data[0];
        if (first) setSelected(first.id);
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selected) return;
    let endpoint;
    if (subjectType === "branch") endpoint = `/branches/${selected}/permissions`;
    else if (subjectType === "role") endpoint = `/role-templates/${selected}/permissions`;
    else endpoint = `/users/${selected}/permissions`;
    api.get(endpoint)
      .then((r) => setPerms(r.data.permissions || {}))
      .catch(() => setPerms({}));
  }, [selected, subjectType]);

  const list = subjectType === "branch" ? branches
    : subjectType === "role" ? Object.keys(ROLE_LABELS).map((r) => ({ id: r, name: ROLE_LABELS[r], role: r }))
    : users;
  const selectedItem = useMemo(() => list.find((u) => u.id === selected), [list, selected]);
  const isAdminUser = subjectType === "staff" && selectedItem?.role === "admin";

  const switchSubject = (t) => {
    setSubjectType(t);
    if (t === "role") {
      setSelected("staff");
      return;
    }
    const arr = t === "branch" ? branches : users;
    const first = t === "staff" ? (arr.find((x) => x.role === "staff") || arr[0]) : arr[0];
    setSelected(first?.id || "");
  };

  const toggle = (module, action) => {
    if (isAdminUser) return;
    setPerms((p) => ({
      ...p,
      [module]: { ...(p[module] || {}), [action]: !((p[module] || {})[action]) },
    }));
  };

  const toggleRow = (module) => {
    if (isAdminUser) return;
    const cur = perms[module] || {};
    const allOn = schema.actions.every((a) => cur[a]);
    const next = {};
    schema.actions.forEach((a) => (next[a] = !allOn));
    setPerms((p) => ({ ...p, [module]: next }));
  };

  const toggleColumn = (action) => {
    if (isAdminUser) return;
    const allOn = schema.modules.every((m) => (perms[m] || {})[action]);
    const next = { ...perms };
    schema.modules.forEach((m) => {
      next[m] = { ...(next[m] || {}), [action]: !allOn };
    });
    setPerms(next);
  };

  // "Grant All" — tick every action on every module for this subject.
  const grantAll = () => {
    if (isAdminUser) return;
    const all = {};
    schema.modules.forEach((m) => {
      all[m] = {};
      schema.actions.forEach((a) => { all[m][a] = true; });
    });
    setPerms(all);
    toast.success("All permissions granted — click Save to persist.");
  };
  const clearAll = () => {
    if (isAdminUser) return;
    const none = {};
    schema.modules.forEach((m) => {
      none[m] = {};
      schema.actions.forEach((a) => { none[m][a] = false; });
    });
    setPerms(none);
  };

  const save = async () => {
    try {
      if (subjectType === "role") {
        const res = await api.put(`/role-templates/${selected}/permissions`, { permissions: perms, apply_to_existing: applyToExisting });
        toast.success(
          applyToExisting
            ? `Role template saved • ${res.data.updated_users} existing users updated`
            : "Role template saved (applies to new users)"
        );
      } else {
        const endpoint = subjectType === "branch" ? `/branches/${selected}/permissions` : `/users/${selected}/permissions`;
        await api.put(endpoint, { permissions: perms });
        toast.success("Permissions saved");
      }
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="role-permissions-page">
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md overflow-hidden border border-slate-200" data-testid="perm-subject-toggle">
          <button
            onClick={() => switchSubject("staff")}
            data-testid="perm-subject-staff"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-2 ${subjectType === "staff" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
          >
            <Users className="h-3.5 w-3.5" /> Staff
          </button>
          <button
            onClick={() => switchSubject("branch")}
            data-testid="perm-subject-branch"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-2 ${subjectType === "branch" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
          >
            <Store className="h-3.5 w-3.5" /> Branch
          </button>
          <button
            onClick={() => switchSubject("role")}
            data-testid="perm-subject-role"
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-2 ${subjectType === "role" ? "bg-[color:var(--brand-navy)] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
          >
            <Layers className="h-3.5 w-3.5" /> Role Template
          </button>
        </div>

        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="h-10 w-72 bg-amber-50 border-amber-300" data-testid="perm-user-select">
            <SelectValue placeholder={`Select ${subjectType}`} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {list.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {subjectType === "staff"
                  ? `${u.name} — ${u.role} (${u.email})`
                  : subjectType === "role"
                  ? `${u.name}`
                  : `${u.name}${u.email ? " (" + u.email + ")" : ""}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {subjectType === "role" && (
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={applyToExisting}
              onChange={(e) => setApplyToExisting(e.target.checked)}
              className="h-4 w-4 accent-[color:var(--brand-teal)]"
              data-testid="perm-apply-existing"
            />
            Also apply to existing {ROLE_LABELS[selected] || "users"}
          </label>
        )}
        <Button
          onClick={save}
          disabled={isAdminUser || !selected}
          className="h-10 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white"
          data-testid="perm-save-btn"
        >
          <Save className="h-4 w-4 mr-1.5" /> Save Permission
        </Button>
        <div className="flex-1" />
        {isAdminUser && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
            * Admins already have full access — no extra permissions needed.
          </div>
        )}
        {subjectType === "branch" && selected && (
          <div className="text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded px-3 py-1.5">
            Setting permissions for branch login — applies to everyone using that branch account.
          </div>
        )}
        {subjectType === "role" && selected && (
          <div className="text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded px-3 py-1.5">
            Default template for <b>{ROLE_LABELS[selected]}</b> — auto-applied to newly created users of this role.
          </div>
        )}
      </div>

      {/* Dedicated bulk-action bar so Grant All / Clear All are impossible to miss on any screen */}
      <div className="bg-gradient-to-r from-emerald-50 to-white border-2 border-emerald-200 rounded-md p-3 flex flex-wrap items-center gap-3" data-testid="perm-bulk-bar">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-800">
          <ShieldIcon className="h-4 w-4" /> Bulk Actions
        </div>
        <div className="text-xs text-slate-600 max-w-md">
          Instantly tick every permission for the selected {subjectType === "role" ? "role" : subjectType === "branch" ? "branch" : "user"} — click <b>Save Permission</b> afterwards to persist.
        </div>
        <div className="flex-1" />
        <Button
          onClick={grantAll}
          disabled={isAdminUser || !selected}
          className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm"
          data-testid="perm-grant-all-btn"
          title="Tick every action on every module"
        >
          <ShieldIcon className="h-4 w-4 mr-1.5" /> Grant All Permissions
        </Button>
        <Button
          onClick={clearAll}
          disabled={isAdminUser || !selected}
          variant="outline"
          className="h-10 px-4 font-semibold border-slate-300"
          data-testid="perm-clear-all-btn"
          title="Untick everything"
        >
          <Square className="h-4 w-4 mr-1.5" /> Clear All
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-slate-700 text-[11px] uppercase tracking-wider">
                <th className="px-3 py-3 text-left font-semibold w-64 sticky left-0 bg-slate-100 z-10">Menu</th>
                {schema.actions.map((a) => (
                  <th key={a} className="px-3 py-3 text-center font-semibold">
                    <button
                      onClick={() => toggleColumn(a)}
                      className="uppercase tracking-wider hover:text-[color:var(--brand-teal)] transition-colors"
                      data-testid={`perm-col-${a}`}
                    >{a}</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {schema.modules.map((m) => {
                const row = perms[m] || {};
                const allOn = schema.actions.every((a) => row[a]);
                return (
                  <tr key={m} className={allOn ? "bg-emerald-50/40" : ""}>
                    <td className="px-3 py-2 sticky left-0 bg-inherit z-10">
                      <button
                        onClick={() => toggleRow(m)}
                        className="text-left font-semibold text-slate-800 hover:text-[color:var(--brand-teal)] transition-colors"
                        data-testid={`perm-row-${m.replace(/\s+/g, "-").toLowerCase()}`}
                      >{m}</button>
                    </td>
                    {schema.actions.map((a) => {
                      const on = !!row[a] || isAdminUser;
                      return (
                        <td key={a} className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggle(m, a)}
                            disabled={isAdminUser}
                            data-testid={`perm-${m.replace(/\s+/g, "-").toLowerCase()}-${a}`}
                            className={`h-6 w-6 rounded border transition-all duration-200 flex items-center justify-center mx-auto ${
                              on
                                ? "bg-[color:var(--brand-teal)] border-[color:var(--brand-teal)] text-white"
                                : "bg-white border-slate-300 hover:border-slate-500"
                            }`}
                          >
                            {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
